import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { motion } from 'framer-motion';
import { RoomTile } from './RoomTile';

interface RoomGridProps {
  rooms: Array<{
    id: string;
    name?: string;
    displayName?: string;
  }>;
  calculated: Record<string, { 
    floorArea: number; 
    wallArea: number; 
    cost: number; 
    itemCount: number;
    density?: number; // for visual scale color
    dimsLabel?: string;
  }>; 
  activeRoomId?: string | null;
  onSelectRoom: (id: string) => void;
  onAddRoom: () => void;
  onDuplicateRoom: (id: string) => void;
  onDeleteRoomRequest: (id: string) => void;
  onUpdateRoomName?: (id: string, name: string) => void;
  onReorderRooms?: (newOrderIds: string[]) => void; // for DnD / move
}

export const RoomGrid: FC<RoomGridProps> = ({
  rooms,
  calculated,
  activeRoomId,
  onSelectRoom,
  onAddRoom,
  onDuplicateRoom,
  onDeleteRoomRequest,
  onUpdateRoomName,
  onReorderRooms,
}) => {
  const { t } = useTranslation();
  const [draggedId, setDraggedId] = useState<string | null>(null);

  const handleDragStart = (id: string) => {
    setDraggedId(id);
  };

  const handleDragOver = (e: React.DragEvent, _overId: string) => {
    e.preventDefault(); // allow drop
  };

  const handleDrop = (e: React.DragEvent, overId: string) => {
    e.preventDefault();
    if (!draggedId || draggedId === overId || !onReorderRooms) {
      setDraggedId(null);
      return;
    }
    const current = rooms.map(r => r.id);
    const fromIdx = current.indexOf(draggedId);
    const toIdx = current.indexOf(overId);
    if (fromIdx === -1 || toIdx === -1) {
      setDraggedId(null);
      return;
    }
    const next = [...current];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    onReorderRooms(next);
    setDraggedId(null);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="font-bold text-sm text-slate-700 dark:text-slate-200">
          {t('editor.rooms_grid_title', 'Помещения (плитки слоёв)')}
        </div>
        {rooms.length > 0 && (
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onAddRoom}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-dashed border-slate-300 dark:border-slate-700 text-xs font-bold text-slate-500 dark:text-slate-400 hover:border-blue-400 hover:text-blue-600 dark:hover:text-blue-400 transition"
            aria-label={t('editor.add_room', 'Добавить комнату')}
            data-testid="add-room-button"
          >
            <Plus size={15} /> {t('editor.add_room', 'Добавить комнату')}
          </motion.button>
        )}
      </div>

      {rooms.length === 0 ? (
        <div
          onClick={onAddRoom}
          className="aspect-[3/1] border border-dashed border-slate-300 dark:border-slate-700 rounded-2xl flex items-center justify-center text-sm text-slate-400 dark:text-slate-500 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-900/50 transition"
          data-testid="add-room-button"
        >
          {t('editor.empty_grid_hint', 'Нажмите, чтобы добавить первое помещение. Плитки дадут быстрый обзор всех слоёв проекта.')}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
          {rooms.map((room, idx) => {
            const cal = calculated[room.id] || { area: 0, cost: 0, itemCount: 0 };
            return (
              <RoomTile
                key={room.id}
                id={room.id}
                name={room.name || room.displayName}
                index={idx}
                floorArea={cal.floorArea}
                wallArea={cal.wallArea}
                cost={cal.cost}
                itemCount={cal.itemCount}
                density={cal.density}
                dimsLabel={cal.dimsLabel}
                isActive={activeRoomId === room.id}
                onClick={() => onSelectRoom(room.id)}
                onDuplicate={onDuplicateRoom}
                onDeleteRequest={onDeleteRoomRequest}
                onUpdateName={onUpdateRoomName}
                draggable={!!onReorderRooms}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RoomGrid;